import { isAxiosError } from "axios";
import { FileSpreadsheet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { DigitalAccessUser, DigitalAccessDecisionValue } from "@/api/digital-access";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import {
  DA_DOC_TYPE_NAMES,
  DA_DOC_TYPES,
  digitalAccessFrame,
  documentsByType,
  type DaDocType,
} from "@/components/projects/diagnostic/selectors/digitalAccess";
import { downloadDigitalAccessWorkbook } from "./exportDigitalAccess";
import { useCurrentUser } from "@/hooks/useAuth";
import { useDigitalAccessDecision, useDigitalAccessUsers } from "@/hooks/useDigitalAccessUsers";
import { formatCount } from "@/lib/format";
import { hasMinRole } from "@/lib/roles";

/** O valor do seletor; `rule` é "sem decisão", que vale a regra da EF. */
type Choice = DigitalAccessDecisionValue | "rule";

const HEAD = "px-2 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-2 py-2 align-top";
const NUMBER = `${CELL} text-right font-mono tabular-nums text-[#08254c]`;

/** Incluir ou excluir mexe no número e pede justificativa; o backend devolve 422 sem ela. */
function needsNote(choice: Choice): boolean {
  return choice === "include" || choice === "exclude";
}

function useTypeLabels(): Record<string, string> {
  const { t } = useTranslation();
  return {
    C: `C · ${t("Communication")}`,
    S: `S · ${t("Service")}`,
    B: `B · ${t("System")}`,
    A: `A · ${t("Dialog")}`,
  };
}

/**
 * Usuários técnicos do Digital Access (#1346 fatia 10, EF-FUNC-005 RF-06).
 *
 * Aberta pelo quadro do Digital Access do Ato 1. Quem conta pela regra (usuário de
 * comunicação, serviço ou sistema) fica numa lista, e os maiores criadores de
 * diálogo, que não contam, em outra: é ali que aparece a integração que roda com
 * usuário de diálogo. O consultor decide com o cliente, usuário a usuário, e o
 * quadro do Ato 1 é relido na hora.
 *
 * A lista é a segunda leitura do Diagnóstico, como a do plano de contas: o quadro
 * traz só o agregado, e o nome de usuário fica atrás desta rota.
 */
export function TechnicalUsersDetail({ sections, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { data, isLoading, refetch } = useDigitalAccessUsers(projectId);
  const canDecide = hasMinRole(useCurrentUser()?.role, "consultant");
  const [estimate, setEstimate] = useState<number | null>(null);

  if (isLoading && !data) {
    return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
  }

  if (!data) {
    return (
      <div
        data-testid="da-users-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the users of the Digital Access.")}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  const byTotal = (a: DigitalAccessUser, b: DigitalAccessUser) => b.total - a.total;
  const counted = data.users.filter((user) => user.counts_by_rule).sort(byTotal);
  const others = data.users.filter((user) => !user.counts_by_rule).sort(byTotal);
  const domain = (validation as Record<string, { state?: string; data?: unknown }> | undefined)
    ?.digital_access;
  const domainTotal =
    domain?.state === "ok"
      ? ((domain.data as { totals?: { da_documents?: number } } | undefined)?.totals?.da_documents ??
        null)
      : null;
  const shownEstimate = estimate ?? domainTotal;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
          {t(
            "Users who created documents of the nine Digital Access types in the period. Integration users count by the rule; the largest dialog creators appear apart, for the case of an integration that runs with a dialog user. Decide with the client whether each one counts.",
          )}
        </p>
        {/* EF-FUNC-005 RF-05 (#1346 fatia 8): itens e usuários numa planilha. */}
        <button
          type="button"
          data-testid="da-export-excel"
          onClick={() =>
            void downloadDigitalAccessWorkbook(digitalAccessFrame(sections, validation), data.users, t)
          }
          className="inline-flex items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc]"
        >
          <FileSpreadsheet aria-hidden className="h-3.5 w-3.5" />
          {t("Export to Excel")}
        </button>
      </div>

      {shownEstimate !== null && (
        <p
          data-testid="da-users-estimate"
          className="text-[13px] font-semibold text-[#08254c]"
        >
          {t("Digital Access with the current decisions: {{value}} documents per year", {
            value: formatCount(Math.round(shownEstimate)),
          })}
        </p>
      )}

      {data.users.length === 0 ? (
        <p
          data-testid="da-users-empty"
          className="rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3 text-[13px] text-[#596b85]"
        >
          {t(
            "The latest Functional run did not count documents per user. Run the Functional again with the client's SAP connection.",
          )}
        </p>
      ) : (
        <>
          <UserTable
            testId="da-users-counted"
            title={t("Integration users (count by the rule)")}
            users={counted}
            canDecide={canDecide}
            projectId={projectId}
            onSaved={setEstimate}
          />
          {others.length > 0 && (
            <UserTable
              testId="da-users-top-creators"
              title={t("Largest dialog creators (do not count by the rule)")}
              users={others}
              canDecide={canDecide}
              projectId={projectId}
              onSaved={setEstimate}
            />
          )}
        </>
      )}

      <p className="text-[11px] leading-snug text-[#7b8799]">
        {t(
          "Excluding or including a user recalculates the Digital Access total in the Act 1 panel right away, and the decision is recorded with who decided and when.",
        )}
      </p>
    </div>
  );
}

function UserTable({
  testId,
  title,
  users,
  canDecide,
  projectId,
  onSaved,
}: {
  testId: string;
  title: string;
  users: DigitalAccessUser[];
  canDecide: boolean;
  projectId: string | undefined;
  onSaved: (estimate: number) => void;
}) {
  const { t } = useTranslation();
  const totalsByUser = users.map((user) => documentsByType(user.by_item));
  // Só os tipos em que alguém desta lista criou documento: nove colunas zeradas
  // empurrariam a decisão para fora da tela.
  const columns = DA_DOC_TYPES.filter((docType) =>
    totalsByUser.some((totals) => (totals[docType] ?? 0) > 0),
  );

  return (
    <section data-testid={testId} className="space-y-2">
      <h3 className="text-[13px] font-semibold text-[#08254c]">{title}</h3>
      {/* O card branco das outras telas de detalhe (pedido do David em 19/10/2026). */}
      <div className="overflow-x-auto rounded-lg border border-[#e3e8ef] bg-white">
        <table className="w-full min-w-[720px] border-collapse text-[12px]">
          <thead>
            <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
              <th className={HEAD}>{t("User")}</th>
              <th className={HEAD}>{t("Type (USTYP)")}</th>
              {columns.map((docType) => (
                <th key={docType} className={`${HEAD} text-right`}>
                  {DA_DOC_TYPE_NAMES[docType]}
                </th>
              ))}
              <th className={`${HEAD} text-right`}>{t("Total")}</th>
              <th className={HEAD}>{t("Count in the Digital Access?")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <UserRow
                key={user.username}
                user={user}
                byType={totalsByUser[index]}
                columns={columns}
                canDecide={canDecide}
                projectId={projectId}
                onSaved={onSaved}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UserRow({
  user,
  byType,
  columns,
  canDecide,
  projectId,
  onSaved,
}: {
  user: DigitalAccessUser;
  byType: Partial<Record<DaDocType, number>>;
  columns: DaDocType[];
  canDecide: boolean;
  projectId: string | undefined;
  onSaved: (estimate: number) => void;
}) {
  const { t } = useTranslation();
  const typeLabels = useTypeLabels();
  const mutation = useDigitalAccessDecision(projectId);
  const current: Choice = user.decision ?? "rule";
  const [draft, setDraft] = useState<Choice | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const labels: Record<Choice, string> = user.counts_by_rule
    ? {
        rule: t("Yes (rule)"),
        exclude: t("No"),
        include: t("Yes"),
        client_validation: t("To confirm with the client"),
      }
    : {
        rule: t("No (rule)"),
        include: t("Yes"),
        exclude: t("No"),
        client_validation: t("To confirm with the client"),
      };
  const options: Choice[] = user.counts_by_rule
    ? ["rule", "exclude", "client_validation"]
    : ["rule", "include", "client_validation"];

  function save(choice: Choice, withNote: string | null) {
    setError(null);
    mutation.mutate(
      {
        username: user.username,
        body: {
          decision: choice === "rule" ? null : choice,
          note: withNote,
          expected_version: user.version,
        },
      },
      {
        onSuccess: (result) => {
          setDraft(null);
          setNote("");
          const totals = (result.estimate as { totals?: { da_documents?: number } }).totals;
          if (typeof totals?.da_documents === "number") onSaved(totals.da_documents);
        },
        onError: (err) => {
          const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
          setError(typeof detail === "string" ? detail : t("Could not save the decision."));
        },
      },
    );
  }

  function choose(choice: Choice) {
    if (needsNote(choice)) {
      setDraft(choice);
      return;
    }
    setDraft(null);
    save(choice, null);
  }

  const selected = draft ?? current;

  return (
    <tr data-testid={`da-user-${user.username}`} className="border-b border-[#eef1f5]">
      <td className={`${CELL} font-mono text-[11px] font-semibold text-[#08254c]`}>
        {user.username}
      </td>
      <td className={`${CELL} text-[#08254c]`}>
        <span>{typeLabels[user.user_type] ?? user.user_type}</span>
      </td>
      {columns.map((docType) => (
        <td key={docType} data-testid={`da-user-type-${docType}`} className={NUMBER}>
          {formatCount(byType[docType] ?? 0)}
        </td>
      ))}
      <td data-testid="da-user-total" className={NUMBER}>
        {formatCount(user.total)}
      </td>
      <td className={`${CELL} min-w-[220px]`}>
        {canDecide ? (
          <div className="space-y-1.5">
            <select
              data-testid="da-user-decision"
              value={selected}
              disabled={mutation.isPending}
              onChange={(event) => choose(event.target.value as Choice)}
              aria-label={t("Count in the Digital Access?")}
              className="w-full rounded-md border border-[#d7dde6] bg-white px-2 py-1 text-[12px] text-[#08254c]"
            >
              {options.map((option) => (
                <option key={option} value={option}>
                  {labels[option]}
                </option>
              ))}
            </select>
            {draft && needsNote(draft) && (
              <div className="space-y-1.5">
                <input
                  data-testid="da-user-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={t("Reason (required)")}
                  maxLength={2000}
                  className="w-full rounded-md border border-[#d7dde6] px-2 py-1 text-[12px]"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="da-user-save"
                    disabled={!note.trim() || mutation.isPending}
                    onClick={() => save(draft, note.trim())}
                    className="rounded-md bg-[#08254c] px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                  >
                    {t("Save")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDraft(null);
                      setNote("");
                    }}
                    className="rounded-md border border-[#d7dde6] px-3 py-1 text-[11px] text-[#596b85]"
                  >
                    {t("Cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <span data-testid="da-user-decision-text" className="text-[#08254c]">
            {labels[current]}
          </span>
        )}
        {user.note && !draft && (
          <div className="mt-1 text-[11px] leading-snug text-[#7b8799]">{user.note}</div>
        )}
        {error && <div className="mt-1 text-[11px] text-[#a23b2f]">{error}</div>}
      </td>
    </tr>
  );
}
